import { Injectable } from "@angular/core";
import { of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { AutomationsService } from "../../core/services/automation.service";
import { ToastifyService } from "../../shared/toastify-service";
import { addAutomation, addAutomationFailure, addAutomationSuccess, deleteAutomation, deleteAutomationFailure, deleteAutomationSuccess, getAutomations, getAutomationsFailure, getAutomationsSuccess, updateAutomation, updateAutomationFailure, updateAutomationSuccess } from "./automation_action";


/**
 * Effects for automation-related actions
 */
@Injectable()
export class AutomationEffects {
  /**
   * Constructor injecting necessary services
   * @param actions$ Observable stream of actions
   * @param restApiService Service for making REST API calls
   * @param toastifyService Service for displaying toast notifications
   */
  constructor(
    private actions$: Actions,
    private restApiService: AutomationsService,
    private toastifyService: ToastifyService,
  ) {}

  /**
   * Effect for creating a new automation
   * @description This effect listens for the 'addAutomation' action and creates a new automation
   *              using the provided postdata
   * @returns Observable that emits when the automation creation is successful or fails
   */
  createAutomation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addAutomation),
      mergeMap(({ postdata }) =>
        this.restApiService.createAutomation(postdata).pipe(
          map(automationrecord => addAutomationSuccess(automationrecord)),
          catchError(error => of(addAutomationFailure({ error })))
        )
      )
    )
  );

  /**
   * Effect for handling successful automation creation
   * @description This effect is triggered when a automation is created successfully
   * @emits No side effects, only shows success message
   */
  addAutomationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addAutomationSuccess),
        tap(() => {
          this.toastifyService.showSuccessMessage("Automation created successfully");
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect for handling failed automation creation
   * @description This effect is triggered when there's an error during automation creation
   * @emits No side effects, only shows error message
   */
  addAutomationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addAutomationFailure),
        tap((error: any) => {
          this.toastifyService.showErrorMessage(error.error.message);
        })
      ),
    { dispatch: false }
  );

  getAutomations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAutomations),
      mergeMap(({postdata}) =>
        this.restApiService.getAutomations(postdata).pipe(
          map(automations => getAutomationsSuccess({ automations })),
          catchError(error => of(getAutomationsFailure({ error })))
        )
      )
    )
  );
  
  /**
   * Effect for deleting an automation
   * @description This effect listens for the 'deleteAutomation' action and deletes the specified automation
   * @returns Observable that emits when the automation deletion is successful or fails
   */
  deleteAutomation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteAutomation),
        mergeMap(({ id }) =>
          this.restApiService.deleteAutomation(id).pipe(
            map(automation => deleteAutomationSuccess({ automation })),
            catchError(error => of(deleteAutomationFailure({ error })))
          )
        )
      )
  );

  /**
   * Effect for handling successful automation deletion
   * @description This effect is triggered when an automation is deleted successfully
   * @emits No side effects, only shows success message
   */
  deleteAutomationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteAutomationSuccess),
        tap(() => {
          this.toastifyService.showSuccessMessage("Automation deleted successfully");
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect for handling failed automation deletion
   * @description This effect is triggered when there's an error during automation deletion
   * @emits No side effects, only shows error message
   */
  deleteAutomationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteAutomationFailure),
        tap((error: any) => {
          this.toastifyService.showErrorMessage(error.error.message);
        })
      ),
    { dispatch: false }
  );
  
  /**
     * Effect for updating an automation
     */
  updateAutomation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateAutomation),
      mergeMap(({ id, postdata }) =>
        this.restApiService.updateAutomation(id, postdata).pipe(
          map(updatedAutomation => updateAutomationSuccess({ updatedAutomation })),
          catchError(error => of(updateAutomationFailure({ error })))
        )
      )
    )
  );

  /**
   * Effect for handling successful automation update
   */
  updateAutomationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateAutomationSuccess),
        tap(() => {
          this.toastifyService.showSuccessMessage("Automation updated successfully");
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect for handling failed automation update
   */
  updateAutomationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateAutomationFailure),
        tap((error: any) => {
          this.toastifyService.showErrorMessage(error.error.message);
        })
      ),
    { dispatch: false }
  );
}